import {Badge, Box, Text, useToast, Menu, MenuItem, MenuButton, Stack, Flex} from '@sanity/ui'
import {useCallback, useState, useMemo} from 'react'
import styled from 'styled-components'
import {ChevronDownIcon} from '@sanity/icons'
import {useStudios} from '@/data/studios/useStudios'
import {Studio} from '@/types/index'
import {Tooltip} from '@/ui/ui-components/primitives/tooltip/Tooltip'
import {truncateString} from '@/utils/general'
import {useFeatureFlagWithContext} from '@/utils/tracking/growthBook'

const AUTO_UPDATE_CHANNEL_ENABLED_FLAG = 'auto-update-channel-enabled'

const AUTO_UPDATE_VERSION_OPTIONS: AutoUpdateVersionOption[] = [
  {
    value: 'latest',
    label: 'Latest',
    title: 'Latest (recommended)',
    description: 'Updated weekly, rolling up all changes from next.',
  },
  {
    value: 'next',
    label: 'Next',
    description: 'Updated daily with every feature and bugfix merged into the Studio codebase.',
  },
  {value: 'stable', label: 'Stable', description: 'A hardened Studio cut monthly.'},
]

const DEFAULT_AUTO_UPDATE_VERSION = 'latest'

const StyledBadge = styled(Badge)`
  position: relative;
`

const BadgeButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-block;
  position: relative;

  &:hover ${StyledBadge} {
    background: ${({theme}) => theme.sanity.color.card.hovered.bg};
    color: ${({theme}) => theme.sanity.color.card.hovered.fg};
  }

  &:hover [data-sanity-icon] {
    color: ${({theme}) => theme.sanity.color.card.hovered.fg};
  }

  &:focus {
    outline: none;
  }

  &:focus ${StyledBadge} {
    box-shadow: 0 0 0 2px var(--card-focus-ring-color);
  }
`

interface VersionSelectProps {
  currentVersion: NonNullable<Studio['autoUpdatingVersion']>
  onVersionChange: (version: NonNullable<Studio['autoUpdatingVersion']>) => void
  studioId: string
}

interface AutoUpdateVersionOption {
  value: NonNullable<Studio['autoUpdatingVersion']>
  label: string
  title?: string
  description: string
}

const VersionSelect = ({currentVersion, onVersionChange, studioId}: VersionSelectProps) => {
  const currentVersionLabel =
    AUTO_UPDATE_VERSION_OPTIONS.find((v) => v.value === currentVersion)?.label || currentVersion

  return (
    <MenuButton
      button={
        <BadgeButton>
          <StyledBadge padding={2}>
            <Flex align="center" gap={2}>
              {currentVersionLabel}
              <ChevronDownIcon />
            </Flex>
          </StyledBadge>
        </BadgeButton>
      }
      id={`auto-update-tag-select-${studioId}`}
      menu={
        <Menu>
          {AUTO_UPDATE_VERSION_OPTIONS.map((version) => (
            <MenuItem
              key={version.value}
              onClick={() => onVersionChange(version.value)}
              selected={currentVersion === version.value}
            >
              <Box padding={0}>
                <Stack space={2}>
                  <Text weight="semibold">{version.title || version.label}</Text>
                  <Text muted size={1}>
                    {version.description}
                  </Text>
                </Stack>
              </Box>
            </MenuItem>
          ))}
        </Menu>
      }
      popover={{
        portal: true,
        placement: 'bottom-end',
        constrainSize: true,
      }}
    />
  )
}

interface VersionBadgeProps {
  studio: Studio
  projectId: string
}

export const VersionBadge = ({studio, projectId}: VersionBadgeProps) => {
  const {activeDeployment, urlType, autoUpdatingVersion, appHost} = studio
  const [selectedVersion, setSelectedVersion] = useState(
    autoUpdatingVersion || DEFAULT_AUTO_UPDATE_VERSION
  )
  const {updateStudioVersionMutation} = useStudios(projectId)
  const toast = useToast()

  const featureFlagContext = useMemo(
    () => ({
      origin: appHost,
    }),
    [appHost]
  )

  const [isAutoUpdateTagEnabled, isFeatureLoaded] = useFeatureFlagWithContext(
    AUTO_UPDATE_CHANNEL_ENABLED_FLAG,
    featureFlagContext
  )

  const handleVersionChange = useCallback(
    (version: NonNullable<Studio['autoUpdatingVersion']>) => {
      setSelectedVersion(version)

      updateStudioVersionMutation.mutate(
        {
          studioId: studio.id,
          autoUpdatingVersion: version,
        },
        {
          onSuccess: () => {
            toast.push({
              status: 'success',
              title: 'Studio auto-update tag updated',
              description: `Studio auto-update tag changed to ${AUTO_UPDATE_VERSION_OPTIONS.find((v) => v.value === version)?.label}`,
            })
          },
          onError: (error) => {
            toast.push({
              status: 'error',
              title: 'Failed to update studio auto-update tag',
              description: error instanceof Error ? error.message : 'Something went wrong',
            })
            // rollback to existing saved tag
            setSelectedVersion(autoUpdatingVersion || DEFAULT_AUTO_UPDATE_VERSION)
          },
        }
      )
    },
    [studio.id, updateStudioVersionMutation, toast, autoUpdatingVersion]
  )

  if (activeDeployment?.isAutoUpdating) {
    if (!isAutoUpdateTagEnabled || !isFeatureLoaded) {
      return (
        <Badge padding={2} style={{whiteSpace: 'nowrap'}}>
          Auto-updating
        </Badge>
      )
    }

    return (
      <VersionSelect
        currentVersion={selectedVersion}
        onVersionChange={handleVersionChange}
        studioId={studio.id}
      />
    )
  } else if (activeDeployment?.version) {
    const truncated = truncateString(activeDeployment.version)
    if (truncated !== activeDeployment.version) {
      return (
        <Tooltip
          content={
            <Text size={1} muted>
              {activeDeployment.version}
            </Text>
          }
          portal
          placement="top"
        >
          <Badge padding={2}>{truncated}</Badge>
        </Tooltip>
      )
    }
    return <Badge padding={2}>{activeDeployment.version}</Badge>
  }

  const tooltipContent = (
    <Box padding={3} style={{maxWidth: 380}}>
      <Text size={1} muted>
        {!activeDeployment && urlType === 'internal'
          ? 'Additional information will populate upon next deployment with an updated CLI'
          : "We don't have information about versions and deployers for externally hosted studios"}
      </Text>
    </Box>
  )
  return (
    <Tooltip content={tooltipContent} portal placement="top">
      <Badge padding={2}>N/A</Badge>
    </Tooltip>
  )
}
